import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import loader from "../../Images/ajax-loader.gif";
import {
  getWastewaterInspection,
  saveWastewaterInspection,
  updateWastewaterInspection
} from "../../api/User";

const translations = {
  en: {
    back: "Back",
    title: "Wastewater Management",
    langToggle: "हिन्दी",
    q1: "1. Status of Wastewater Management",
    good: "Good",
    satisfactory: "Satisfactory",
    needsImprovement: "Needs Improvement",
    q2: "2. What is the current system for the drainage of wastewater in the village?",
    covered: "Covered Drainage System",
    open: "Open Drainage System",
    soakPits: "Soak Pits",
    naturalWaterBodies: "Discharged into Natural Water Bodies (Pond/Canal/River)",
    noProperSystem: "No Proper Drainage System",
    others: "Other",
    othersSpecify: "Please specify other drainage system details",
    q3: "3. Is water logging observed due to improper wastewater management?",
    yes: "Yes",
    no: "No",
    q4: "4. If yes, please mention the location(s) of water logging.",
    waterLoggingSpecify: "Location of water logging",
    q5: "5. Does the existing wastewater management system require cleaning, repair, or upgradation?",
    q6: "6. If yes, please mention the location and nature of the work required.",
    natureWorkSpecify: "Location and nature of the work required",
    cancel: "Cancel",
    save: "Save",
    update: "Update",
    saving: "Saving...",
    validationError: "Please resolve the validation errors in the form.",
    drainageSystemError: "Please select at least one drainage system",
    waterLoggingLocationError: "Please specify the water logging location",
    repairDetailsError: "Please specify location and nature of the work required",
    othersError: "Please specify other drainage system details",
    confirmTitle: "Confirm Save",
    confirmMsgSave: "After saving you will allow only edit once. Do you want to proceed?",
    confirmMsgUpdate: "You will not be allowed to edit this form after saving. Do you want to proceed?",
    okay: "Okay",
  },
  hi: {
    back: "पीछे",
    title: "अपशिष्ट जल प्रबंधन",
    langToggle: "English",
    q1: "1. अपशिष्ट जल प्रबंधन की स्थिति",
    good: "अच्छी",
    satisfactory: "संतोषजनक",
    needsImprovement: "सुधार की आवश्यकता है",
    q2: "2. गाँव में अपशिष्ट जल की निकासी के लिए वर्तमान व्यवस्था क्या है?",
    covered: "कवर्ड ड्रेनेज सिस्टम (ढकी हुई नाली व्यवस्था)",
    open: "Open Drainage System (खुली नाली व्यवस्था)",
    soakPits: "सोक पिट्स (सोखता गड्ढा)",
    naturalWaterBodies: "प्राकृतिक जल निकायों (तालाब/नहर/नदी) में विसर्जन",
    noProperSystem: "कोई उचित जलनिकासी व्यवस्था नहीं",
    others: "अन्य",
    othersSpecify: "कृपया अन्य निकासी प्रणाली स्पष्ट करें",
    q3: "3. क्या अनुचित अपशिष्ट जल प्रबंधन के कारण जलभराव देखा जाता है?",
    yes: "हाँ",
    no: "नहीं",
    q4: "4. यदि हाँ, तो कृपया जलभराव वाले स्थान (स्थानों) का उल्लेख करें।",
    waterLoggingSpecify: "जलभराव का स्थान",
    q5: "5. क्या वर्तमान अपशिष्ट जल प्रबंधन प्रणाली को सफाई, मरम्मत या सुधार की आवश्यकता है?",
    q6: "6. यदि हाँ, तो कृपया आवश्यक कार्य की प्रकृति और स्थान का उल्लेख करें।",
    natureWorkSpecify: "कार्य की प्रकृति और स्थान",
    cancel: "रद्द करें",
    save: "सहेजें",
    update: "अपडेट करें",
    saving: "सहेज रहा है...",
    validationError: "कृपया फॉर्म में त्रुटियों को ठीक करें।",
    drainageSystemError: "कृपया कम से कम एक जल निकासी प्रणाली का चयन करें",
    waterLoggingLocationError: "कृपया जलभराव का स्थान निर्दिष्ट करें",
    repairDetailsError: "कृपया कार्य की प्रकृति और स्थान निर्दिष्ट करें",
    othersError: "कृपया अन्य निकासी प्रणाली का विवरण दर्ज करें",
    confirmTitle: "बदलाव सहेजें",
    confirmMsgSave: "सहेजने के बाद आपको केवल एक बार एडिट करने की अनुमति होगी। क्या आप आगे बढ़ना चाहते हैं?",
    confirmMsgUpdate: "इस बार सहेजने के बाद आपको इस फॉर्म को दोबारा एडिट करने की अनुमति नहीं होगी। क्या आप आगे बढ़ना चाहते हैं?",
    okay: "ठीक है",
  }
};

const WastewaterForm = () => {
  const { meetingId } = useParams();
  const [searchParams] = useSearchParams();
  const gpId = searchParams.get("gp");
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user_id");

  const [lang, setLang] = useState("en"); // English default
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [isFrozen, setIsFrozen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  // Form Fields
  const [wastewaterStatus, setWastewaterStatus] = useState("Satisfactory");
  const [drainageSystem, setDrainageSystem] = useState({
    covered: false,
    open: false,
    soakPits: false,
    naturalWaterBodies: false,
    noProperSystem: false,
    others: false,
  });
  const [otherDrainageDetails, setOtherDrainageDetails] = useState("");
  const [waterLogging, setWaterLogging] = useState(false);
  const [waterLoggingLocation, setWaterLoggingLocation] = useState("");
  const [requireRepair, setRequireRepair] = useState(false);
  const [repairDetails, setRepairDetails] = useState("");

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const loadRecord = async () => {
      try {
        setLoading(true);
        const res = await getWastewaterInspection(token, meetingId);
        if (res.status === "success" && res.data) {
          const data = res.data;
          setRecordId(data.id);
          setWastewaterStatus(data.wastewater_status || "Satisfactory");

          // Parse drainage system JSON
          const systems = data.drainage_system || [];
          setDrainageSystem({
            covered: systems.includes("Covered Drainage System"),
            open: systems.includes("Open Drainage System"),
            soakPits: systems.includes("Soak Pits"),
            naturalWaterBodies: systems.includes("Discharged into Natural Water Bodies (Pond/Canal/River)"),
            noProperSystem: systems.includes("No Proper Drainage System"),
            others: systems.some(s => s.startsWith("Others:")),
          });
          const otherStr = systems.find(s => s.startsWith("Others:"));
          if (otherStr) {
            setOtherDrainageDetails(otherStr.replace("Others: ", ""));
          }

          setWaterLogging(!!data.water_logging);
          setWaterLoggingLocation(data.water_logging_location || "");
          setRequireRepair(!!data.require_repair);
          setRepairDetails(data.repair_details || "");

          // Handle freeze logic
          if (data.updated_by !== null && data.updated_by !== 0) {
            setIsFrozen(true);
          }
        }
      } catch (error) {
        console.error("Error loading wastewater inspection details:", error);
        toast.error("Failed to load existing inspection details.", { autoClose: 2000 });
      } finally {
        setLoading(false);
      }
    };

    if (meetingId) {
      loadRecord();
    }
  }, [meetingId, token]);

  useEffect(() => {
    const role = sessionStorage.getItem("role_id");
    if (["1", "3", "7", "8"].includes(role)) {
      setIsFrozen(true);
    }
  }, []);

  const handleDrainageChange = (event) => {
    if (isFrozen) return;
    setDrainageSystem({
      ...drainageSystem,
      [event.target.name]: event.target.checked,
    });
  };

  const isFormValid = () => {
    if (isFrozen) return false;

    // Q2: Must have at least one checkbox checked
    const hasDrainage = drainageSystem.covered || drainageSystem.open || drainageSystem.soakPits || drainageSystem.naturalWaterBodies || drainageSystem.noProperSystem || drainageSystem.others;
    if (!hasDrainage) return false;
    
    // Q2 Other specify details check
    if (drainageSystem.others && !otherDrainageDetails.trim()) return false;

    // Q3 Check
    if (waterLogging && !waterLoggingLocation.trim()) return false;

    // Q5 Check
    if (requireRepair && !repairDetails.trim()) return false;

    return true;
  };

  const validate = () => {
    const errors = {};
    const hasDrainage = drainageSystem.covered || drainageSystem.open || drainageSystem.soakPits || drainageSystem.naturalWaterBodies || drainageSystem.noProperSystem || drainageSystem.others;
    
    if (!hasDrainage) {
      errors.drainageSystem = translations[lang].drainageSystemError;
    }
    if (drainageSystem.others && !otherDrainageDetails.trim()) {
      errors.otherDrainageDetails = translations[lang].othersError;
    }
    if (waterLogging && !waterLoggingLocation.trim()) {
      errors.waterLoggingLocation = translations[lang].waterLoggingLocationError;
    }
    if (requireRepair && !repairDetails.trim()) {
      errors.repairDetails = translations[lang].repairDetailsError;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    if (isFrozen) return;
    if (!validate()) {
      toast.error(translations[lang].validationError, { autoClose: 2000 });
      return;
    }
    setOpenConfirm(true);
  };

  const executeSave = async () => {
    setOpenConfirm(false);
    try {
      setSaving(true);
      // Compile drainage system list
      const drainageList = [];
      if (drainageSystem.covered) drainageList.push("Covered Drainage System");
      if (drainageSystem.open) drainageList.push("Open Drainage System");
      if (drainageSystem.soakPits) drainageList.push("Soak Pits");
      if (drainageSystem.naturalWaterBodies) drainageList.push("Discharged into Natural Water Bodies (Pond/Canal/River)");
      if (drainageSystem.noProperSystem) drainageList.push("No Proper Drainage System");
      if (drainageSystem.others && otherDrainageDetails.trim()) {
        drainageList.push(`Others: ${otherDrainageDetails.trim()}`);
      }

      const payload = {
        meeting_id: parseInt(meetingId),
        gram_panchayat_id: parseInt(gpId || sessionStorage.getItem("gp_data") || 0),
        wastewater_status: wastewaterStatus,
        drainage_system: drainageList,
        water_logging: waterLogging,
        water_logging_location: waterLogging ? waterLoggingLocation : "",
        require_repair: requireRepair,
        repair_details: requireRepair ? repairDetails : "",
      };

      let res;
      if (recordId) {
        payload.updated_by = parseInt(userId);
        res = await updateWastewaterInspection(token, recordId, payload);
      } else {
        payload.created_by = parseInt(userId);
        res = await saveWastewaterInspection(token, payload);
      }

      if (res.status === "success") {
        toast.success(res.message || "Saved successfully!", { autoClose: 1500 });
        setTimeout(() => {
          navigate("/gram-sabha-inspection");
        }, 1500);
      } else {
        toast.error(res.message || "Failed to save form record.");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("An error occurred while saving the form.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <img src={loader} alt="loading..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", width: "100%" }}>
      <ToastContainer />

      {/* Confirmation Modal */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title" style={{ fontWeight: "bold" }}>
          {translations[lang].confirmTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" style={{ color: "#333" }}>
            {recordId ? translations[lang].confirmMsgUpdate : translations[lang].confirmMsgSave}
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "15px 24px" }}>
          <Button 
            onClick={() => setOpenConfirm(false)} 
            variant="outlined" 
            style={{ textTransform: "none", color: "#666", borderColor: "#ccc" }}
          >
            {translations[lang].cancel}
          </Button>
          <Button 
            onClick={executeSave} 
            variant="contained" 
            autoFocus
            style={{ textTransform: "none", backgroundColor: "#4763E4", color: "#fff" }}
          >
            {translations[lang].okay}
          </Button>
        </DialogActions>
      </Dialog>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Button 
            onClick={() => navigate("/gram-sabha-inspection")} 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            style={{ textTransform: "none", color: "#4763E4", borderColor: "#4763E4" }}
          >
            {translations[lang].back}
          </Button>
          <h3 style={{ margin: 0, fontWeight: "bold" }}>
            {translations[lang].title}
          </h3>
        </div>
        <Button 
          onClick={() => setLang(lang === "en" ? "hi" : "en")} 
          variant="outlined" 
          style={{ textTransform: "none", color: "#4763E4", borderColor: "#4763E4", fontWeight: "bold" }}
        >
          {translations[lang].langToggle}
        </Button>
      </div>
      <hr style={{ border: "1px solid #ddd", marginBottom: "30px" }} />

      
      {!isFrozen && recordId && (
        <div style={{
          backgroundColor: "#FEF3C7",
          color: "#92400E",
          border: "1px solid #FCD34D",
          padding: "15px",
          borderRadius: "6px",
          marginBottom: "25px",
          fontWeight: "bold",
          fontSize: "14px"
        }}>
          ⚠️ {lang === "en" ? "Note: You can edit this form only one time." : "ध्यान दें: आप इस फॉर्म को केवल एक ही बार एडिट कर सकते हैं।"}
        </div>
      )}

      <form onSubmit={handleSaveClick} style={{ background: "#fff", padding: "30px", borderRadius: "8px", boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)", width: "100%" }}>
        
        {/* 1. Status of Wastewater Management */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q1}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={wastewaterStatus} onChange={(e) => setWastewaterStatus(e.target.value)}>
              <FormControlLabel value="Good" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].good} />
              <FormControlLabel value="Satisfactory" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].satisfactory} />
              <FormControlLabel value="Needs Improvement" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].needsImprovement} />
            </RadioGroup>
          </FormControl>
        </div>

        {/* 2. What is the current system for the drainage of wastewater in the village? */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "10px" }}>{translations[lang].q2}</h5>
          <FormGroup row style={{ display: "flex", flexDirection: "column" }}>
            <FormControlLabel
              control={<Checkbox checked={drainageSystem.covered} onChange={handleDrainageChange} name="covered" color="primary" disabled={isFrozen} />}
              label={translations[lang].covered}
            />
            <FormControlLabel
              control={<Checkbox checked={drainageSystem.open} onChange={handleDrainageChange} name="open" color="primary" disabled={isFrozen} />}
              label={translations[lang].open}
            />
            <FormControlLabel
              control={<Checkbox checked={drainageSystem.soakPits} onChange={handleDrainageChange} name="soakPits" color="primary" disabled={isFrozen} />}
              label={translations[lang].soakPits}
            />
            <FormControlLabel
              control={<Checkbox checked={drainageSystem.naturalWaterBodies} onChange={handleDrainageChange} name="naturalWaterBodies" color="primary" disabled={isFrozen} />}
              label={translations[lang].naturalWaterBodies}
            />
            <FormControlLabel
              control={<Checkbox checked={drainageSystem.noProperSystem} onChange={handleDrainageChange} name="noProperSystem" color="primary" disabled={isFrozen} />}
              label={translations[lang].noProperSystem}
            />
            <FormControlLabel
              control={<Checkbox checked={drainageSystem.others} onChange={handleDrainageChange} name="others" color="primary" disabled={isFrozen} />}
              label={translations[lang].others}
            />
          </FormGroup>
          {drainageSystem.others && (
            <TextField
              label={translations[lang].othersSpecify}
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: "10px" }}
              value={otherDrainageDetails}
              onChange={(e) => setOtherDrainageDetails(e.target.value)}
              disabled={isFrozen}
              error={!!formErrors.otherDrainageDetails}
              helperText={formErrors.otherDrainageDetails}
            />
          )}
        </div>

        {/* 3. Is water logging observed due to improper wastewater management? */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q3}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={waterLogging ? "Yes" : "No"} onChange={(e) => setWaterLogging(e.target.value === "Yes")}>
              <FormControlLabel value="Yes" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].yes} />
              <FormControlLabel value="No" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].no} />
            </RadioGroup>
          </FormControl>
          {waterLogging && (
            <TextField
              label={translations[lang].waterLoggingSpecify}
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: "10px" }}
              value={waterLoggingLocation}
              onChange={(e) => setWaterLoggingLocation(e.target.value)}
              disabled={isFrozen}
              error={!!formErrors.waterLoggingLocation}
              helperText={formErrors.waterLoggingLocation}
            />
          )}
        </div>

        {/* 5. Does the existing wastewater management system require cleaning, repair, or upgradation? */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q5}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={requireRepair ? "Yes" : "No"} onChange={(e) => setRequireRepair(e.target.value === "Yes")}>
              <FormControlLabel value="Yes" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].yes} />
              <FormControlLabel value="No" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].no} />
            </RadioGroup>
          </FormControl>
          {requireRepair && (
            <TextField
              label={translations[lang].natureWorkSpecify}
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: "10px" }}
              value={repairDetails}
              onChange={(e) => setRepairDetails(e.target.value)}
              disabled={isFrozen}
              error={!!formErrors.repairDetails}
              helperText={formErrors.repairDetails}
            />
          )}
        </div>

        {/* Submit */}
        <div style={{ display: "flex", gap: "15px", justifyContent: "flex-end", alignItems: "center" }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate("/gram-sabha-inspection")}
            disabled={saving}
            style={{ textTransform: "none", color: "#666", borderColor: "#ccc" }}
          >
            {translations[lang].cancel}
          </Button>
          {!isFrozen && (
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !isFormValid()}
              style={{ textTransform: "none", backgroundColor: isFormValid() ? "#4763E4" : "#ccc" }}
            >
              {saving ? translations[lang].saving : recordId ? translations[lang].update : translations[lang].save}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default WastewaterForm;
